import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { tasksApi, UpdateTaskData, type TaskVisibilityScope } from '@/lib/api';
import { getEffectiveLastUpdated } from '@/lib/task-last-updated';
import { toast } from '@/components/ui/toaster';
import type { Task, TaskHistoryEntry } from '@/types';

/** Extended task type with project info for followed tasks view. */
export interface FollowedTask extends Task {
  project?: { id: string; name: string };
}

function normalizeTask(task: Task): Task {
  return {
    ...task,
    assignees: task.assignees ?? [],
    effectiveLastUpdated: getEffectiveLastUpdated(task)?.toISOString() ?? null,
  };
}

/**
 * Keep project task list caches in sync after PATCH /tasks/:id.
 * invalidateQueries alone can leave the board/list stale until refetch completes (or if refetch is skipped);
 * merging the response immediately fixes the slide-over close UX.
 */
function patchProjectTaskListCaches(queryClient: QueryClient, projectId: string, updated: Task, taskId: string) {
  const queries = queryClient.getQueryCache().findAll({ queryKey: ['tasks', projectId] });

  for (const q of queries) {
    const key = q.queryKey;
    if (!Array.isArray(key) || key.length < 3) continue;
    const listIdKey = key[2];
    if (typeof listIdKey !== 'string') continue;

    queryClient.setQueryData<Task[]>(key, (old) => {
      if (!old || !Array.isArray(old)) return old;

      if (updated.listId !== listIdKey) {
        return old.filter((t) => t.id !== taskId);
      }

      const idx = old.findIndex((t) => t.id === taskId);
      if (idx === -1) {
        return [...old, normalizeTask(updated)];
      }
      const next = [...old];
      next[idx] = normalizeTask({ ...next[idx], ...updated });
      return next;
    });
  }
}

export interface CreateSubtaskData {
  title: string;
  description?: string;
  listId?: string;
  statusId: string;
  priority?: string;
  assigneeIds?: string[];
  assigneeId?: string | null;
  assignedTeamId?: string | null;
  dueDate?: string | null;
}

export function useTasks(
  projectId: string | undefined,
  listId?: string,
  options?: { visibilityScope?: TaskVisibilityScope },
) {
  const visibilityScope = options?.visibilityScope ?? 'all';
  const listIdTrimmed = listId?.trim();
  const enabled = Boolean(projectId && listIdTrimmed);

  return useQuery({
    queryKey: ['tasks', projectId, listIdTrimmed, visibilityScope],
    queryFn: async () => {
      if (!projectId || !listIdTrimmed) throw new Error('Project ID and list ID required');
      const response = await tasksApi.getByProject(projectId, {
        listId: listIdTrimmed,
        visibilityScope,
      });
      const raw = response.data;
      const rows = Array.isArray(raw) ? raw : [];
      return rows.map((t) => normalizeTask(t as Task));
    },
    enabled,
  });
}

export function useTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID required');
      const response = await tasksApi.getById(taskId);
      return normalizeTask(response.data as Task);
    },
    enabled: !!taskId,
  });
}

export function useTaskHistory(taskId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['tasks', 'history', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID required');
      const response = await tasksApi.getHistory(taskId);
      return response.data as TaskHistoryEntry[];
    },
    enabled: !!taskId && enabled,
  });
}

export interface CreateTaskData {
  listId: string;
  statusId: string;
  title: string;
  description?: string;
  priority?: string;
  assigneeIds?: string[];
  assigneeId?: string;
  assignedTeamId?: string;
  dueDate?: string;
  parentId?: string;
  isClientVisible?: boolean;
  tagIds?: string[];
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateTaskData;
    }) => {
      const response = await tasksApi.create(projectId, data);
      return response.data as Task;
    },
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', task.projectId, task.listId] });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      projectId: string;
      data: UpdateTaskData;
    }) => {
      const response = await tasksApi.update(id, data);
      const payload = response.data as Task | { success: true; taskId: string; accessAfterMove: false; message: string };
      return payload;
    },
    onSuccess: (payload, variables) => {
      if ('accessAfterMove' in payload && payload.accessAfterMove === false) {
        void queryClient.invalidateQueries(
          variables.projectId
            ? { queryKey: ['tasks', variables.projectId] }
            : { queryKey: ['tasks'] }
        );
        void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.id] });
        toast({ title: payload.message ?? 'Task moved successfully.' });
        return;
      }

      const task = normalizeTask(payload as Task);
      queryClient.setQueryData(['tasks', 'detail', variables.id], task);

      if (variables.projectId) {
        patchProjectTaskListCaches(queryClient, variables.projectId, task, variables.id);
      }

      // Keep board/list stable after field edits by avoiding broad refetches here.
      // Caches are patched above with the mutation response payload.
    },
  });
}

export function useCreateSubtask(parentTaskId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSubtaskData) => {
      if (!parentTaskId) throw new Error('Parent task ID required');
      const response = await tasksApi.createSubtask(parentTaskId, data);
      return response.data as Task;
    },
    onSuccess: (subtask) => {
      if (parentTaskId) {
        queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', parentTaskId] });
      }
      queryClient.invalidateQueries({ queryKey: ['tasks', subtask.projectId] });
    },
  });
}

export function useMoveTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      id: string;
      projectId: string;
      listId?: string;
      statusId: string;
      position?: number;
    }) => {
      const response = await tasksApi.move(variables.id, {
        statusId: variables.statusId,
        position: variables.position,
      });
      return response.data as Task;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.projectId, variables.listId] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string; projectId: string; listId?: string }) => {
      await tasksApi.delete(variables.id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.projectId, variables.listId] });
    },
  });
}

export interface MoveTaskToResult {
  movedToListId?: string;
  movedToProjectId?: string;
  lostAccess?: boolean;
}

export function useMoveTaskTo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      id: string;
      sourceProjectId: string;
      targetProjectId: string;
      targetListId: string;
      targetStatusId: string;
    }) => {
      const response = await tasksApi.moveTo(variables.id, {
        projectId: variables.targetProjectId,
        listId: variables.targetListId,
        statusId: variables.targetStatusId,
      });
      return response.data as Task | MoveTaskToResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.sourceProjectId] });
      if (variables.sourceProjectId !== variables.targetProjectId) {
        queryClient.invalidateQueries({ queryKey: ['tasks', variables.targetProjectId] });
      }
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.id] });
    },
  });
}

export interface MergeTaskResult {
  success: boolean;
  sourceTaskId: string;
  targetTaskId: string;
  message: string;
  warnings?: string[];
}

export function useMergeTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      sourceTaskId: string;
      targetTaskId: string;
      sourceProjectId: string;
      targetProjectId: string;
    }) => {
      const response = await tasksApi.merge(variables.sourceTaskId, variables.targetTaskId);
      return response.data as MergeTaskResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.sourceProjectId] });
      if (variables.sourceProjectId !== variables.targetProjectId) {
        queryClient.invalidateQueries({ queryKey: ['tasks', variables.targetProjectId] });
      }
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.sourceTaskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.targetTaskId] });
    },
  });
}

// ============================================
// TASK FOLLOWING / WATCHERS
// ============================================

/**
 * Get all tasks followed by the current user.
 */
export function useFollowedTasks() {
  return useQuery({
    queryKey: ['tasks', 'following'],
    queryFn: async () => {
      const response = await tasksApi.getFollowing();
      const raw = response.data;
      const rows = Array.isArray(raw) ? raw : [];
      return rows.map((t) => normalizeTask(t as Task)) as FollowedTask[];
    },
  });
}

/**
 * Follow a task.
 */
export function useFollowTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await tasksApi.follow(taskId);
      return response.data as { success: boolean };
    },
    onSuccess: (_, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'following'] });
    },
  });
}

/**
 * Unfollow a task.
 */
export function useUnfollowTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await tasksApi.unfollow(taskId);
      return response.data as { success: boolean };
    },
    onSuccess: (_, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'following'] });
    },
  });
}
